import React, { useContext } from 'react';
import classnames from 'classnames';
import { useDataModel } from '@thd-nucleus/data-sources';
import { Typography, Skeleton, SkeletonContent, SkeletonLine } from '@one-thd/sui-atomic-components';
import {
  bool, string
} from 'prop-types';
import { SponsoredProductListing } from '@thd-olt-component-react/sponsored-content';
import { dataModel } from '../../dataModel';
import { BulletList } from '../BulletList';
import { InfoGuides } from '../InfoGuides';
import { shouldDisplayAdditionalResources, getBullets } from '../../helpers';
import { Highlights } from '../Highlights';
import { ProductInfo } from '../ProductInfo/ProductInfo';
import { RichContent } from '../RichContent/RichContent';

const DesktopContent = (props) => {
  const {
    itemId,
    hideRichContents,
    showRichContentsOnly,
    showReturnMessage,
    showPla
  } = props;

  const { data, loading, error } = useDataModel('product', { variables: { itemId } });

  if (loading) {
    return (
      <Skeleton
        grow
      >
        <SkeletonContent disablePadding>
          <SkeletonLine numberOfLines={8} fullWidth />
        </SkeletonContent>
      </Skeleton>
    );
  }

  if ((error && !data) || !data?.product) {
    return null;
  }
  const { product } = data;

  const {
    prop65Warning,
    returnable
  } = product?.info || {};

  const {
    description,
    additionalResources,
    descriptiveAttributes,
    highlights
  } = product?.details || {};

  const {
    modelNumber,
    rentalCategory,
    rentalSubCategory,
    storeSkuNumber,
    upc
  } = product?.identifiers;

  const bullets = getBullets({
    channel: 'desktop',
    descriptiveAttributes,
    showRichContentsOnly,
    showReturnMessage,
    prop65Warning,
    returnable
  });

  const displayAdditionalResources = !!(
    additionalResources
    && shouldDisplayAdditionalResources(additionalResources)
    && !showRichContentsOnly
  );
  const displayBullets = bullets && bullets.length > 0;

  return (
    <div id="product-overview-desktop-content" className="sui-w-full sui-text-base" data-testid="desktop-content">
      {!hideRichContents && <RichContent itemId={itemId} />}
      <div className="sui-flex sui-flex-col sui-gap-2 md:sui-flex-row">
        <div className={classnames('sui-flex sui-flex-col sui-gap-4', {
          'sui-w-full md:sui-w-7/12': displayAdditionalResources,
          'sui-w-full': !displayAdditionalResources
        })}
        >
          <div className="sui-flex sui-flex-col sui-gap-4">
            <Highlights
              hideRichContents={hideRichContents}
              highlights={highlights}
            />
            <span className="sui-mt-8">
              <Typography variant="h5" weight="bold">
                About This Product
              </Typography>
            </span>
            <p>{description}</p>
          </div>
          {displayBullets && <BulletList bullets={bullets} margin isMobile={false} />}
          <ProductInfo
            itemId={itemId}
            upc={upc}
            rentalCategory={rentalCategory}
            rentalSubcategory={rentalSubCategory}
            modelNumber={modelNumber}
          />
        </div>
        {displayAdditionalResources && (
          <div className="sui-w-full sui-flex sui-flex-col sui-gap-2 md:sui-w-5/12">
            <InfoGuides
              additionalResources={additionalResources}
              returnMessage={returnable}
              showReturnMessage={showReturnMessage}
            />
            {showPla && (
              <div data-testid="pla-wrapper">
                <SponsoredProductListing
                  pageContext={
                    {
                      label: 'product-overview',
                      data: { itemId }
                    }
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const areEqual = (prevProps, nextProps) => {
  return prevProps.itemId === nextProps.itemId
    && prevProps.returnMessage === nextProps.returnMessage;
};

DesktopContent.displayName = 'DesktopContent';
DesktopContent.dataModel = dataModel;

DesktopContent.propTypes = {
  itemId: string.isRequired,
  hideRichContents: bool,
  showRichContentsOnly: bool,
  showReturnMessage: bool,
  showPla: bool
};

DesktopContent.defaultProps = {
  hideRichContents: false,
  showRichContentsOnly: false,
  showReturnMessage: false,
  showPla: false
};

export default DesktopContent;
